import {
  Box,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useLineSheetOrderSheetItems from "@/features/line-sheet-sets/detail/hooks/use-line-sheet-order-sheet-items";
import {
  CONFIRMED_COLOR,
  ORDERED_COLOR,
} from "@/features/line-sheet-sets/detail/line-sheet-order-sheet-table";
import {
  OrderTotalExportFormat,
  OrderTotalExportQuantity,
} from "@/features/line-sheet-sets/hooks/use-boutique-order-total-export";

export interface BoutiqueOrderTotalExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  lineSheetSetId: number;
  lineSheetId: number;
  lineSheetName: string;
  format: OrderTotalExportFormat;
  quantity: OrderTotalExportQuantity;
  // 고른 오더(오더시트세트) id 들로 합계 파일을 만든다
  onConfirm: (orderSheetSetIds: number[]) => void;
}

const LINE_COLOR = "#EFEFED";
const MUTED_COLOR = "#8F8F8C";

/**
 * (부티크) 라인시트 줄의 합계 다운로드 — 합칠 오더를 고르는 모달.
 *
 * 오더 목록은 라인시트세트 상세가 쓰는 훅과 같은 데이터라 다시 받지 않는다(SWR 키가 같다).
 * 취소된 오더는 합계에 들어가지 않아 보여주지 않고, 확정 수량 합계는 확정 수량이 들어간 오더만 고를 수 있다.
 * 열 때마다 고를 수 있는 오더를 전부 고른 상태로 시작한다(기존 '모든 오더' 동작과 같다).
 */
export default function BoutiqueOrderTotalExportModal({
  isOpen,
  onClose,
  lineSheetSetId,
  lineSheetId,
  lineSheetName,
  format,
  quantity,
  onConfirm,
}: BoutiqueOrderTotalExportModalProps) {
  const { t, tTitle, tLocalDateString } = useI18nHelper();
  const { itemsByLineSheetId, isLoading } = useLineSheetOrderSheetItems(
    lineSheetSetId,
    isOpen
  );
  const items = useMemo(
    () =>
      (itemsByLineSheetId.get(lineSheetId) ?? []).filter(
        (item) => item.orderSheet.status !== "CANCELED"
      ),
    [itemsByLineSheetId, lineSheetId]
  );
  const selectableIds = useMemo(
    () =>
      items
        .filter(
          (item) =>
            quantity === "ORDERED" || item.orderSheet.confirmedQuantity > 0
        )
        .map((item) => item.orderSheetSet.id),
    [items, quantity]
  );
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  // 열릴 때(또는 오더 목록이 도착할 때) 전부 고른 상태로 되돌린다
  const selectableKey = selectableIds.join(",");
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(selectableKey ? selectableKey.split(",").map(Number) : []);
    }
  }, [isOpen, selectableKey]);

  const allSelected =
    selectableIds.length > 0 && selectedIds.length === selectableIds.length;
  const toggle = (orderSheetSetId: number, checked: boolean) => {
    setSelectedIds((prev) =>
      checked
        ? prev.includes(orderSheetSetId)
          ? prev
          : [...prev, orderSheetSetId]
        : prev.filter((id) => id !== orderSheetSetId)
    );
  };
  const quantityText = (value: number) =>
    `${value.toLocaleString()}${t("quantity")}`;
  const formatLabel = t(
    `common:boutique_order_total.format_${
      format === "ORIGIN" ? "origin" : "uno_form"
    }`
  );
  const quantityLabel = t(
    quantity === "ORDERED" ? "ordered_quantity" : "confirmed_quantity"
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size={"lg"}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {tTitle("common:boutique_order_total.select_orders")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex flexDirection={"column"} gap={"12px"}>
            <Box>
              <Text textStyle={"body-md-regular"} color={"#6F6F6C"}>
                {lineSheetName}
              </Text>
              <Text fontSize={"12px"} color={MUTED_COLOR}>
                {`${formatLabel} · ${quantityLabel}`}
              </Text>
              <Text fontSize={"12px"} color={MUTED_COLOR}>
                {t("common:boutique_order_total.select_orders_hint")}
              </Text>
            </Box>
            {isLoading && items.length === 0 ? (
              <Flex justifyContent={"center"} padding={"24px"}>
                <Spinner size={"sm"} />
              </Flex>
            ) : items.length === 0 ? (
              <Text padding={"24px 0"} textAlign={"center"} color={MUTED_COLOR}>
                {t("common:boutique_order_total.no_orders")}
              </Text>
            ) : (
              <Flex flexDirection={"column"}>
                <Flex
                  padding={"8px 4px"}
                  borderBottom={`1px solid ${LINE_COLOR}`}
                >
                  <Checkbox
                    isChecked={allSelected}
                    isIndeterminate={selectedIds.length > 0 && !allSelected}
                    isDisabled={selectableIds.length === 0}
                    onChange={(event) =>
                      setSelectedIds(event.target.checked ? selectableIds : [])
                    }
                  >
                    <Text fontSize={"12px"}>{tTitle("select_all")}</Text>
                  </Checkbox>
                </Flex>
                <Box maxHeight={"360px"} overflowY={"auto"}>
                  {items.map(({ orderSheetSet: set, orderSheet }) => {
                    const isSelectable = selectableIds.includes(set.id);
                    return (
                      <Checkbox
                        key={set.id}
                        width={"100%"}
                        padding={"8px 4px"}
                        borderBottom={`1px solid ${LINE_COLOR}`}
                        alignItems={"center"}
                        isChecked={selectedIds.includes(set.id)}
                        isDisabled={!isSelectable}
                        onChange={(event) =>
                          toggle(set.id, event.target.checked)
                        }
                        sx={{
                          "& > .chakra-checkbox__label": {
                            flex: 1,
                            minWidth: 0,
                          },
                        }}
                      >
                        <Flex alignItems={"center"} gap={"12px"}>
                          <Box flex={1} minWidth={0}>
                            <Text fontWeight={"500"} noOfLines={1}>
                              {set.createdByCompany?.name ?? "-"}
                            </Text>
                            <Text fontSize={"10px"} color={MUTED_COLOR}>
                              {`#${set.id}`}
                              {set.submittedAt &&
                                ` · ${tLocalDateString(
                                  new Date(set.submittedAt)
                                )}`}
                            </Text>
                          </Box>
                          <Box textAlign={"end"} whiteSpace={"nowrap"}>
                            <Text fontSize={"12px"} color={ORDERED_COLOR}>
                              {quantityText(orderSheet.orderedQuantity)}
                            </Text>
                            <Text fontSize={"12px"} color={CONFIRMED_COLOR}>
                              {orderSheet.confirmedQuantity > 0
                                ? quantityText(orderSheet.confirmedQuantity)
                                : t("unconfirmed")}
                            </Text>
                          </Box>
                        </Flex>
                      </Checkbox>
                    );
                  })}
                </Box>
              </Flex>
            )}
          </Flex>
        </ModalBody>
        <ModalFooter gap={"8px"}>
          <AppButton variant={"gray"} onClick={onClose}>
            {tTitle("cancel")}
          </AppButton>
          <AppButton
            variant={"primary"}
            isDisabled={selectedIds.length === 0}
            onClick={() => {
              onConfirm(selectedIds);
              onClose();
            }}
          >
            {tTitle("common:boutique_order_total.download_selected", {
              n: selectedIds.length,
            })}
          </AppButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
